import { Component, ChangeDetectionStrategy, inject } from '@angular/core';
import { UntypedFormGroup, UntypedFormControl, Validators } from '@angular/forms';
import { takeUntil } from 'rxjs/operators';
import { TuiSizeL, TuiSizeS } from '@taiga-ui/core';
import { TranslateService } from '@ngx-translate/core';
import { AlertService, DocumentService } from '@src/core/services';
import { EnvService } from '@src/app/modules/env';
import { BaseResizableComponent } from '@src/app/modules/base';

import { FormData, FormDataControls, ImportData } from './types';
import { ImportService } from './services';

@Component({
  selector: 'app-import-users',
  templateUrl: './import-users.component.html',
  styleUrls: ['./import-users.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ImportUsersComponent extends BaseResizableComponent {
  private readonly importService = inject(ImportService);
  private readonly alertService = inject(AlertService);
  private readonly env = inject(EnvService);
  private readonly documentService = inject(DocumentService);
  private readonly translateService = inject(TranslateService);

  loading = false;

  tuiElementSize: TuiSizeS | TuiSizeL = 'm';

  form = new UntypedFormGroup(<FormDataControls>{
    file: new UntypedFormControl(undefined, Validators.required),
  });

  override ngOnInit(): void {
    super.ngOnInit();

    this.importService
      .getResult()
      .pipe(takeUntil(this.destroyed$$))
      .subscribe(response => {
        if (response?.body) {
          this.form.reset();
          if (response.body.size === 0) {
            this.alertService.success(
              this.translateService.instant('components.importUsers.alerts.successes.fileImport'),
            );
          } else {
            this.alertService.warning(
              this.translateService.instant('components.importUsers.alerts.errors.fileImport'),
              {
                autoClose: 30000,
              },
            );
            this.env.downloadFileByHttpResponse(response);
          }
        }

        this.loading = false;
        this.cdr.markForCheck();
      });
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();

    this.importService.clearImportData();
  }

  onClickSaveButton() {
    this.form.markAllAsTouched();

    if (this.form.invalid) {
      this.alertService.error('Загрузите файл');
      return;
    }

    this.loading = true;
    const data = this.mapFormDataToApiData(this.form.value);
    this.importService.startImport(data);
    this.cdr.markForCheck();
  }

  private mapFormDataToApiData(formData: FormData): ImportData {
    const importData: ImportData = {
      files: [formData.file],
    };

    return importData;
  }
}
